// Labels and tones for Growth › Campaigns.

import type { CampaignStatus, ScheduleState, SendStatus } from '@/api/growthCampaignTypes'
import type { BadgeTone } from '@/components/ui'

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  running: 'Running',
  paused: 'Paused',
  done: 'Done',
}

export const CAMPAIGN_STATUS_TONE: Record<CampaignStatus, BadgeTone> = { draft: 'outline', scheduled: 'accent', running: 'good', paused: 'warn', done: 'neutral' }

/**
 * A campaign row's status. Scheduling the campaign approved it, so `queued` reads
 * Scheduled; `pending_approval` is a row from before that rule, which the next plan
 * approves.
 */
export const SEND_STATUS_LABELS: Record<SendStatus, string> = {
  pending_approval: 'Scheduled',
  queued: 'Scheduled',
  sending: 'Sending',
  sent: 'Sent',
  dry_run: 'Dry run',
  skipped: 'Skipped',
  removed: 'Removed',
  failed: 'Failed',
}

export const SEND_STATUS_TONE: Record<SendStatus, BadgeTone> = {
  pending_approval: 'accent',
  queued: 'accent',
  sending: 'accent',
  sent: 'good',
  dry_run: 'neutral',
  skipped: 'outline',
  removed: 'outline',
  failed: 'bad',
}

/** How an email on the Schedule stands. */
export const SCHEDULE_STATE_LABELS: Record<ScheduleState, string> = {
  scheduled: 'Scheduled',
  sending: 'Sending',
  sent: 'Sent',
  dry_run: 'Dry run',
  failed: 'Failed',
  removed: 'Removed',
  skipped: 'Skipped',
}

export const SCHEDULE_STATE_TONE: Record<ScheduleState, BadgeTone> = {
  scheduled: 'accent',
  sending: 'accent',
  sent: 'good',
  dry_run: 'neutral',
  failed: 'bad',
  removed: 'outline',
  skipped: 'outline',
}

const SKIP_REASONS: Record<string, string> = {
  lead_deleted: 'The lead was deleted',
  no_email: 'No email address',
  address_changed: 'The address changed after planning',
  not_us: 'Outside the US',
  undeliverable: "That domain can't receive mail",
  suppressed: 'On the do-not-contact list',
  lead_status: 'The lead is no longer open to contact',
  activity_status: "The activity check changed, and this campaign doesn't include them now",
  bucket: "Their bucket changed, and this campaign doesn't include it",
  sequence_stopped: 'They replied, bounced or unsubscribed',
  recently_emailed: 'Emailed recently by another campaign',
  missing_merge_field: "A merge field has no value for this lead",
  subject_refused: 'The subject merged into something misleading',
  replied: 'They replied',
  unsubscribed: 'They unsubscribed',
  bounced: 'An earlier message bounced',
  removed: 'Removed from the Schedule',
  preview_ended: 'Its preview link stopped working',
  not_scheduled: 'Taken off the Schedule',
}

const GATE_REASONS: Record<string, string> = {
  sending_off: 'Sending is off',
  not_on_allowlist: 'Not on the test list',
}

const SEND_ERRORS: Record<string, string> = {
  interrupted: 'Interrupted mid-send. It may have gone, so it was not retried.',
  internal: 'Something broke. It was not retried.',
  // A one-off email to a lead, at its last look or taken off the Schedule by an event.
  missed: 'The server was down at its time, so it was not sent late.',
  suppressed: 'On the do-not-contact list',
  lead_status: 'The lead is on the do-not-contact list',
  lead_deleted: 'The lead was deleted',
  address_changed: "The lead's address changed",
  not_us: 'The lead is outside the US',
  preview_ended: 'Its preview link stopped working',
  smtp_missing: "The mailbox isn't set up",
  unsubscribed: 'They unsubscribed',
  bounced: 'An earlier message bounced',
}

export function skipReasonLabel(reason: string | null): string {
  return reason ? (SKIP_REASONS[reason] ?? reason.replace(/_/g, ' ')) : ''
}

export function gateReasonLabel(reason: string | null): string {
  return reason ? (GATE_REASONS[reason] ?? reason) : ''
}

export function sendErrorLabel(error: string | null): string {
  return error ? (SEND_ERRORS[error] ?? `Not delivered (${error})`) : ''
}

/** Why a row reads as it does, in a sentence. Empty when its status says it all. */
export function sendDetail(row: { skip_reason: string | null; gate_reason: string | null; error: string | null }): string {
  return skipReasonLabel(row.skip_reason) || gateReasonLabel(row.gate_reason) || sendErrorLabel(row.error)
}

export function percent(rate: number): string {
  return `${(Math.round(rate * 1000) / 10).toString()}%`
}

/** The time zones Ops is likely to want, offered first. Any IANA zone is accepted. */
export const COMMON_TIME_ZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu']
